import { Navigate } from 'react-router-dom';

import { appSection } from '@/shared/navigation/appSections';
import type { AppSectionKey } from '@/shared/navigation/appSections';
import { useSectionAccess } from '@/shared/navigation/useSectionAccess';

import { AccessRefusal } from './AccessRefusal';

interface SectionIndexRedirectProps {
  /**
   * Where this workspace's front door may lead, best first. The first one the session can enter wins.
   */
  candidates: AppSectionKey[];
}

/**
 * The front door of a workspace whose sub-areas need different permissions.
 *
 * <p><b>Why a component and not a `<Navigate>`.</b> `/catalog` and `/loyalty` used to redirect to a
 * hard-coded first child — `/catalog/products`, `/loyalty/customers`. But catalogue is four
 * permissions, not one, and `/loyalty/customers` is a *customer* search: a pricing-only merchandiser
 * opening `/catalog`, or a loyalty-only employee opening `/loyalty`, was bounced straight into a
 * refusal for a screen that was never theirs — while the workspace they *could* use sat one path away.
 * The redirect knew nothing about the session, so it sent everyone to the same door.
 *
 * <p>Refusing only when *no* candidate opens means the dead end is now reserved for people who really
 * have no business here, and they still get the standard way onward.
 */
export function SectionIndexRedirect({ candidates }: SectionIndexRedirectProps) {
  const { canEnter } = useSectionAccess();
  const target = candidates.find(canEnter);

  if (!target) {
    return <AccessRefusal />;
  }
  return <Navigate to={appSection(target).path} replace />;
}
